<script lang="ts" setup>
  import { ref, computed, type Ref } from 'vue';
  import { useShared, useTranslator } from '@othercommerce/foundation';
  import { useWishlistProducts, addToWishlistEvent, useGoogleTagManager } from '@storefront/storefront';
  import type { ProductListingResource, ComparatorProductVariantResource, OrderItemWithStock, ProductVariantResource } from '@storefront/resources';

  interface Props {
    variant: number;
    variantModel: ProductListingResource | ComparatorProductVariantResource | OrderItemWithStock | ProductVariantResource;
  }

  const props = defineProps<Props>();

  const shared = useShared();
  const gtm = useGoogleTagManager();
  const translator = useTranslator();

  const variants: Ref<number[]> = useWishlistProducts();
  const open = ref(false);

  const saved = computed(() => {
    return variants.value.includes(props.variant);
  });

  const savedText = computed(() => {
    return saved.value ? translator.trans('SHOP.COMMON.ARIA.PRODUCT_SAVED_TO_WISHLIST') : translator.trans('SHOP.COMMON.ARIA.ADD_TO_WISHLIST');
  });

  function onSaved() {
    open.value = false;
    gtm.push(addToWishlistEvent(props.variantModel, shared.value.activeCurrencyCode));
  }
</script>
<template>
  <div class="wishlist" @click.prevent.stop="open = true">
    <slot v-bind="{ saved }">
      <SfButton :class="{ saved }" class="wishlist__button" rounded square transparent :aria-label="savedText" :title="savedText" aria-haspopup="dialog" aria-controls="wishlist-picker-modal">
        <SfIconHeartFill v-if="saved" aria-hidden="true" />
        <SfIconHeart v-else aria-hidden="true" />
      </SfButton>
    </slot>
    <UiModal medium v-model:open="open" id="wishlist-picker-modal" aria-labelledby="wishlist-picker-modal-title">
      <SfModalHeader v-model:open="open" headerType="span" headingStyle headingId="wishlist-picker-modal-title">
        {{ $t('SHOP.COMMON.ACTION.ADD_TO_WISHLIST') }}
      </SfModalHeader>
      <WishlistPickerModal :variant="variant" @saved="onSaved" />
    </UiModal>
  </div>
</template>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .wishlist {
    display: contents;
    font-size: 1.125rem;

    &__button {

      &:focus-visible {
        z-index: 1;
      }

      &.saved {
        color: var(--danger-color);

        @media (hover: hover) {
          &:hover {
            color: var(--danger-color-hover);
          }
        }
      }
    }
  }
</style>
